"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowLeft, LoaderCircle, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { env } from "@/env";
import { clearSession } from "./actions";

export default function ClearSessionPage() {
  const router = useRouter();
  const [clearing, setClearing] = useState(false);

  const handleClear = async () => {
    setClearing(true);
    try {
      await clearSession();
    } catch {
      // Cookie deletion is best-effort — redirect to sign-in regardless.
    }
    router.push("/sign-in");
  };

  return (
    <div className="flex h-screen flex-col items-center justify-center bg-background">
      <div className="w-full max-w-[400px] overflow-hidden rounded-2xl border border-border bg-secondary shadow-2xl">
        {/* Logo — matches sign-in page */}
        <div className="flex flex-col items-center pb-2 pt-8">
          <Image
            src="/logo.svg"
            alt="Logo"
            width={56}
            height={56}
            className="mb-3"
          />
          <span className="text-xs font-semibold tracking-[0.25em] text-muted-foreground">
            {env.NEXT_PUBLIC_APP_NAME}
          </span>
        </div>

        <div className="space-y-5 px-8 pb-8 pt-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <ShieldAlert className="h-6 w-6 text-destructive" />
          </div>
          <div className="space-y-1">
            <h1 className="text-xl font-bold text-foreground">
              Clear your session
            </h1>
            <p className="text-sm text-muted-foreground">
              If you&apos;re having trouble signing in, clearing your session
              data can help. This will sign you out and remove all cached
              authentication data.
            </p>
          </div>
          <Button
            onClick={handleClear}
            disabled={clearing}
            className="h-10 w-full rounded-lg text-sm font-semibold"
          >
            {clearing ? (
              <>
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                Clearing…
              </>
            ) : (
              "Clear session & sign in again"
            )}
          </Button>
          <div className="text-sm">
            <Link
              href="/sign-in"
              className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
